// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * fix-sprint-20261004-63-8-2 F8 — program governance's own cadence: a
 * reporting period's cadence and a governance model's review frequency, as the
 * reporting-period service stores and validates them. Its own list, not the
 * access-policy review frequency (`ACCESS_POLICY_REVIEW_FREQUENCIES`), which
 * happens to hold the same four values today and belongs to another register.
 */
export const PROGRAM_GOVERNANCE_CADENCES = [
    'monthly',
    'quarterly',
    'semi_annual',
    'annual',
];
/**
 * Declared as a runtime array, not only a type — Sprint 65.8.0.
 *
 * `shared-vocabulary-census` found three services transcribing these members
 * into local `z.enum([...])` literals. They had no choice: the union existed as
 * a TYPE alias with nothing importable at runtime, so a Zod schema could only
 * restate it. A restated union is one edit from disagreeing with the union it
 * validates for, and the census's own docblock records how that bites — a
 * schema one member narrower than its union silently refuses a legal value.
 */
export const EVIDENCE_GAP_SEVERITIES = ['critical', 'high', 'medium', 'low'];
/**
 * `planned → in_fieldwork → reporting → closed`, with `cancelled` reachable
 * from any state that is not already terminal.
 *
 * `closed` and `cancelled` are terminal. The transitions are enforced
 * server-side in `AuditEngagementService.transition`, not by the UI: a
 * lifecycle a client can skip is not a lifecycle.
 */
export const AUDIT_ENGAGEMENT_STATES = [
    'planned',
    'in_fieldwork',
    'reporting',
    'closed',
    'cancelled',
];
// ── Audit Findings (Sprint 65.8.0, AC2) ─────────────────────────────
/**
 * `open → accepted → remediating → closed`, plus `rejected`.
 *
 * `rejected` is reachable from `open` ONLY. Rejection is the organisation
 * saying the observation is not a finding; once it has been `accepted` that
 * statement has been made in the other direction, and un-making it by
 * transition would leave the register unable to say which of the two
 * happened. A finding accepted in error is closed with a reason, not rejected.
 *
 * `closed` and `rejected` are the terminal states, and they are the two that
 * make a finding RESOLVED for the purposes of closing its engagement — see
 * `AuditEngagementService`.
 */
export const AUDIT_FINDING_STATES = [
    'open',
    'accepted',
    'remediating',
    'closed',
    'rejected',
];
/**
 * The review frequencies a RACI assignment accepts. One list read by the backend
 * schema and by the RACI page's select, so the page cannot offer a value the
 * server refuses. Distinct from the governance model's `ReviewFrequency`.
 */
export const RACI_REVIEW_FREQUENCIES = ['monthly', 'quarterly', 'annually'];
export const REMEDIATION_PRIORITIES = ['critical', 'high', 'medium', 'low'];
// ── Sprint 65.8.0 (AC3) — what a remediation plan is allowed to point at ──
//
// R-43, argued before the code. The platform already had THREE corrective-action
// registers and a fourth is forbidden:
//
// | register | anchor | scope |
// |---|---|---|
// | `RemediationAction` (`incident.ts`) | `incident_id` required | incidents |
// | `RemediationPlan` (here) | `gap_id` required | evidence gaps |
// | `CapaRecord` (`manufacturing.ts`) | product / batch | sector overlay |
//
// None could anchor to an audit finding, because each requires an id of a
// different kind. So the decision was never "which register" but **what the
// register is allowed to point at** — and widening one anchor is one change to
// one type, where a parallel `grc_corrective_actions` collection beside it is
// exactly the duplication R-43 exists to stop.
//
// The shape, the states and the verification semantics are UNCHANGED.
/**
 * The kinds of record a remediation plan may be raised against.
 *
 * Two members, deliberately closed. A third belongs here only when a third
 * register would otherwise be minted for it.
 */
export const REMEDIATION_SOURCE_KINDS = ['evidence_gap', 'audit_finding'];
//# sourceMappingURL=program-governance.js.map